import { Gym, GymTypes, IEnabledFeatures, ILeaderboardInstance,
  IUserLeaderboardInstance,
  IUserWorkoutAggregate,
  IUserWorkoutSessionSummary,
  LeaderboardData,
  NextWorkout,
} from 'fitbeat.models';
import { IExternalReference, UserWorkoutSession, WorkoutSettings } from '../../entities';
import { IWorkoutRepository } from './interfaces/iworkout-repository';

export abstract class BaseSerializedWorkoutRepository implements IWorkoutRepository {
  public abstract getAll(gym: Gym): Promise<UserWorkoutSession[]>;
  public abstract add(userWorkoutSession: UserWorkoutSession): Promise<void>;
  public addOrUpdate(userWorkoutSession: UserWorkoutSession): Promise<void> {
    throw new Error('Method not implemented.');
  }
  public getWorkoutSessionFromId(workoutId: string): Promise<UserWorkoutSession | undefined> {
    throw new Error('Method not implmented.');
  }
  public getFromExternalReferences(references: IExternalReference): Promise<UserWorkoutSession | undefined> {
    throw new Error('Method not implmented.');
  }
  public remove(userWorkoutSession: UserWorkoutSession): Promise<void> {
    throw new Error('remove Method not implemented.');
  }
  public update(userWorkoutSession: UserWorkoutSession): Promise<void> {
    throw new Error('update Method not implemented.');
  }

  public bulkUpdate(userWorkoutSession: UserWorkoutSession[]): Promise<UserWorkoutSession[]> {
    throw new Error('bulkUpdate Method not implemented.');
  }

  public updateWorkoutInstructorSetting(uniqueReference: string, instructorId: string): Promise<void> {
    throw new Error('Method not implemented.');
  }

  public updateWorkoutSettings(uniqueReference: string, workoutSettings: WorkoutSettings): Promise<void> {
    throw new Error('update mutiple method not implemented.');
  }

  public getNonCheckedInAndNotRemoveWorkoutsStartedBefore(time: Date): Promise<UserWorkoutSession[]> {
      throw new Error('getNonCheckedInAndNotRemoveWorkoutsStartedBefore Method not implemented');
  }

  public get(
    gym: Gym,
    userId?: string,
    from?: Date,
    to?: Date,
    filter?: (sessions: UserWorkoutSession[]) => UserWorkoutSession[]): Promise<UserWorkoutSession[]> {
    throw new Error('get Method not implemented.');
  }

  public getExpiredAndNotNotifiedAndNotRemovedProvisionalSessions(
    dateToCheckFor: Date,
    filter?: ((sessions: UserWorkoutSession[]) => UserWorkoutSession[])): Promise<UserWorkoutSession[]> {
    throw new Error('Method not implemented.');
  }

  public async getConfirmedBookingsAndInvitesAndProvisional(
    userId?: string,
  ): Promise<UserWorkoutSession[]> {
    throw new Error('Method not implemented');
  }

  public async getWorkoutHistory(userProfileId: string, after: Date): Promise<IUserWorkoutSessionSummary[]> {
    throw new Error('Method not implemented');
  }

  public async getWorkoutsAfterPointInTime(userProfileId: string, from: Date): Promise<UserWorkoutSession[]> {
    throw new Error('Method not implemented');
  }

  public async getHistoryBetweenDates(userId: string, from: Date, to: Date): Promise<IUserWorkoutSessionSummary[]> {
    throw new Error('Method not implemented');
  }

  public async getWorkoutsCurrentlyActiveInStudio(
    userProfileId: string,
    now: Date,
  ): Promise<UserWorkoutSession> {
    throw new Error('Method not implemented');
  }

  public async getLastWorkout(userProfileId: string, from: Date, to: Date): Promise<UserWorkoutSession | undefined> {
    throw new Error('Method not implemented');
  }

  public async getWorkoutSessionToNotifyForReview(now: Date): Promise<UserWorkoutSession[]> {
    throw new Error('Method not implemented');
  }

  public async getWorkoutsWithFirstExerciseOn(now: Date): Promise<UserWorkoutSession[]> {
    throw new Error('Method not implemented');
  }

  public async getAllWorkoutsForUsers(userProfileIds: string[], from: Date, to: Date): Promise<UserWorkoutSession[]> {
    throw new Error('Method not implemented');
  }

  public async getAllWorkoutsForSingleUser(
    userProfileId: string,
    includeRemovedWorkouts?: boolean,
    limit?: number,
    includeProvisional?: boolean): Promise<UserWorkoutSession[]> {
    throw new Error('Method not implemented');
  }

  public async getNextWorkout(userProfileId: string, after: Date): Promise<NextWorkout | undefined> {
    throw new Error('Method not implemented.');
  }

  public async getAllPairedWorkouts(workoutId: string, requestTime: Date): Promise<UserWorkoutSession[]> {
    throw new Error('Method not implemented.');
  }

  public async getWorkoutsForLeaderboard(from: Date): Promise<UserWorkoutSession[]> {
    throw new Error('Method not implemented.');
  }

  public getAllActiveInstructors(): Promise<string[]> {
    throw new Error('Method not implemented.');
  }

  public getUserWorkoutAggregates(
    gymName: string,
    tz: string,
    workoutsDateRange?: { workoutsFromDate: Date, workoutsToDate?: Date },
  ): Promise<IUserWorkoutAggregate[]> {
    throw new Error('Method not implemented.');
  }

  public getUserWorkoutCount(userProfileId: string): Promise<number> {
    throw new Error('Method not implemented.');
  }

  public async getUserWorkoutCountForGym(
    uid: string,
    gymName: string,
  ): Promise<number> {
    throw new Error('Method not implemented.');
  }

  public getLastWorkoutWorkoutSelectionId(
    uid: string,
    settings: WorkoutSettings,
    gymType: GymTypes,
    shouldIncludeUnderscore: boolean = true,
  ):
  Promise<{ startTime: Date; workoutSelectionIndex: number; workoutSelectionKey: string; } | undefined> {
    throw new Error('Method not implemented.');
  }

  public getFutureBookingsForPtDashboard(
    startingFrom: Date,
    endingAt: Date,
    gymName: string): Promise<UserWorkoutSession[]> {
    throw new Error('Method not implemented.');
  }

  public getAllSessionsInDateRangeForGym(from: Date, to: Date, gym: Gym): Promise<UserWorkoutSession[]> {
    throw new Error('Method not implemented.');
  }

  public getWorkoutSessionFromIdWithPairedSession(workoutId: string): Promise<UserWorkoutSession | undefined> {
    throw new Error('Method not implemented');
  }

  public getForUserThatEndedInTimeRange(from: Date, to: Date, userProfileId: string): Promise<UserWorkoutSession[]> {
    throw new Error('Method not implemented');
  }

  public updateWorkoutSessionSettings(workoutId: string, uid: string,
                                      enabledFeatures: IEnabledFeatures, workoutSettings: WorkoutSettings):
    Promise<void> {
    throw new Error('Method not implemented');
  }

  public getWorkoutsForStudioLeaderboard(
    from: Date,
    to: Date,
    gymName: string): Promise<UserWorkoutSession[]> {
      throw new Error('Method not implemented');
    }

  public getLeaderboardData(
    leaderboardInstance: ILeaderboardInstance,
    showIndividual: boolean = true,
    team?: string,
  ): Promise<LeaderboardData> {
    throw new Error('Method not implemented.');
  }

  public getLeaderboardsForUser(userId: string, fromDate: Date): Promise<IUserLeaderboardInstance[]> {
    throw new Error('Method not implemented.');
  }

  public getNotCompletedNotRemovedWorkouts(time: Date): Promise<UserWorkoutSession[]> {
    throw new Error('Method not implemented.');
  }
}
